import { baseGlobals } from '@root/typescript/baseGlobals'

import { is } from 'stdlib/is'

import DOM from '../DOM/DOM'

import UserAgent from '../UserAgent/UserAgent'

import { ANIMATIONS } from './animations'
import type { TAnim, TAnimType } from './animations'

export type TAnimName<T extends string = string> = TAnimType | T
export type TElement = Element | null

export interface IAnimRunProps {
  readonly el:               TElement
  readonly name:             TAnimName
  readonly duration:         number
  readonly isForce?:         boolean
  readonly willRemoveClass?: boolean
}

const ua = UserAgent.getInstance()

export default class Anim {
  public static readonly ANIMS: TAnim = ANIMATIONS

  /**
   * Animáció indítása.
   * @throws
   * @param props - Az animáció paraméterei.
   */
  public static run (props: IAnimRunProps): Promise<void> {
    const { duration, el, isForce = false, name, willRemoveClass = true } = props

    const isFlipIn  = name === ANIMATIONS.FlipInX || name === ANIMATIONS.FlipInY
    const isFlipOut = name === ANIMATIONS.FlipOutX
    const isFlip    = isFlipIn || isFlipOut

    // TODO: ternary miatt (is) getAnimName
    let animName = name

    // iOS-en ezeket az animációkat nem használhatjuk!
    if (isFlip) {
      animName = ua.isIOS || ua.isIPadOS
        ? isFlipIn
          ? ANIMATIONS.FadeIn
          : ANIMATIONS.FadeOut
        : name
    }

    // Nem megfelelő animáció név van megadva, TypeError-ral elszáll
    if (!animName) {
      throw new TypeError(`[Anim] Ismeretlen animáció: ${ animName }!`)
    }

    // Ha az `el` nem null és nem HTMLElement-ből extendál akkor mehet a TypeError `isDev` alatt
    if (baseGlobals.isDev && !is.null(el) && !is.instanceOf(el, HTMLElement)) {
      const textStart = '[Anim] Ezt az elemet nem lehet animálni:'

      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, no-restricted-syntax, @typescript-eslint/no-base-to-string -- Itt ezek kellenek.
      throw new TypeError(`${ textStart } ${ typeof el }, értéke: ${ el }, ezzel az animációval: ${ animName }`)
    }

    if (is.instanceOf(el, HTMLElement)) {
      // Ha van elemünk és nincs még beállítva duration VAGY force-olni akarjuk.
      if (el.style.animationDuration === '' || isForce) {
        el.style.animationDuration = `${ duration }ms`
      }

      // Teszteléskor nem kell az animáció.
      if (baseGlobals.isTest) {
        DOM.dispatch(el, 'animationend')

        if (!willRemoveClass) {
          el.classList.add(animName)
        }

        return Promise.resolve()
      }

      // Innentől a normál működés van, felrakjuk a classt, lefut az animáció és resolve.
      return new Promise((resolve) => {
        DOM.once(el, 'animationend', (e: Event) => {
          e.stopPropagation()

          if (willRemoveClass) {
            el.classList.remove(animName)
          }

          resolve()
        })

        el.classList.add(animName)
      })
    }

    return Promise.resolve()
  }
}
